import { ensureSubscriptionActionAllowed } from '../utils/subscriptionActionGuard';
import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useRef,
} from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  message,
  Tabs,
  Tag,
  InputNumber,
  Row,
  Col,
  Alert,
  TableProps,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  UndoOutlined,
  SearchOutlined,
  CloseCircleOutlined,
  EditOutlined,
  FileExcelOutlined,
  SaveOutlined,
  ReloadOutlined,
  BarChartOutlined,
  WalletOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axios from "axios";
import "./GestionRetourAchat.css";
import * as XLSX from "xlsx";
import { useAuth } from "../context/AuthContext";

// Ajouter l'import de useSelection depuis SelectionContext
import { useSelection } from "../SelectionContext";

// Importer le hook personnalisé
import { usePermission } from "../utils/permissionUtils";
import RowActionsMenu from "../components/RowActionsMenu";
import OrderInitialPaymentFields, {
  buildInitialPaymentPayload,
} from "../components/orders/OrderInitialPaymentFields";
import {
  CommercialDocumentAdditionalInfo,
  CommercialDocumentModalTitle,
  CommercialDocumentSummary,
} from "../components/orders/CommercialDocumentShell";
import {
  buildCommercialEntitySearchText,
  getCommercialEntityDisplayName,
  matchesCommercialEntitySearch,
} from "../utils/entityUtils";
import ReturnPurchaseDetailModal from "./components/ReturnPurchaseDetailModal";
import ReturnPurchasePaymentModal from "./components/ReturnPurchasePaymentModal";
import CommercialListSummary from "../components/orders/CommercialListSummary";
import "../components/orders/CommercialListPage.css";

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

const QUANTITY_DECIMAL_PLACES = 5;
const MIN_RETURN_QUANTITY = 0.00001;

// Interfaces
interface Fournisseur {
  id: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  company_id: number;
  user_id?: number; // Ajouter cette propriété facultative
}

interface Product {
  id: number;
  name: string;
  item_code: string;
  purchase_price: number;
  sales_price: number;
  current_stock: number;
  warehouse_name?: string;
  categorie_nom?: string;
  unit_name?: string;
  unit_short_name?: string;
  taxe_name?: string;
  taxe_rate?: number;
}

interface RetourAchatItem {
  id?: number;
  product_id: number;
  product_name: string;
  quantity: number;
  max_quantity?: number; // Ajouter cette propriété pour stocker la quantité originale
  unit_price: number;
  discount_rate: number;
  tax_rate: number;
  tax_amount: number;
  subtotal: number;
  original_order_id?: number;
  original_order_item_id?: number;
  selectOpen?: boolean; // Ajouter cette propriété
}

interface RetourAchat {
  id: number;
  invoice_number: string;
  reference: string;
  order_date: string;
  total: number;
  due_amount: number;
  paid_amount: number;
  payment_status: string;
  order_status: string;
  user_id: number;
  user_name: string;
  warehouse_id: number;
  warehouse_name: string;
  company_id: number;
  items?: RetourAchatItem[];
  shipping_cost?: number;
  discount_amount?: number;
  discount_type?: string;
  tax_amount?: number;
  tax_id?: number;
  tax_rate?: number;
  notes?: string;
  terms_condition?: string;
  created_at?: string;
  original_order_id?: number;
  is_deleted?: number;
  is_deletable?: number;
}

interface Tax {
  id: number;
  name: string;
  rate: number;
}

interface Payment {
  id: number;
  payment_number: string;
  date: string;
  amount: number;
  payment_mode_name: string;
  remarks?: string;
}

interface Warehouse {
  id: number;
  name: string;
  is_active: boolean;
  company_id: number;
}

// État et actions pour le reducer
interface RetourAchatsState {
  retourAchats: RetourAchat[];
  fournisseurs: Fournisseur[];
  produits: Product[];
  taxes: Tax[];
  loading: boolean;
  currentRetourAchat: RetourAchat | null;
  retourAchatItems: RetourAchatItem[];
  orderPayments: Payment[];
  warehouses: Warehouse[];
  totalItems: number;
  currentPage: number;
  error: string | null;
  currentWarehouse: any;
  activeTab?: string; // Ajouter l'état activeTab
}

type RetourAchatsAction =
  | {
      type: "FETCH_RETOUR_ACHATS_SUCCESS";
      payload: RetourAchat[];
      totalItems: number;
    }
  | { type: "FETCH_FOURNISSEURS_SUCCESS"; payload: Fournisseur[] }
  | { type: "FETCH_PRODUITS_SUCCESS"; payload: Product[] }
  | { type: "FETCH_TAXES_SUCCESS"; payload: Tax[] }
  | { type: "FETCH_WAREHOUSES_SUCCESS"; payload: Warehouse[] }
  | { type: "SET_CURRENT_RETOUR_ACHAT"; payload: RetourAchat }
  | { type: "SET_RETOUR_ACHAT_ITEMS"; payload: RetourAchatItem[] }
  | { type: "SET_ORDER_PAYMENTS"; payload: Payment[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string }
  | { type: "SET_CURRENT_PAGE"; payload: number }
  | { type: "SET_CURRENT_WAREHOUSE"; payload: any }
  | { type: "SET_ACTIVE_TAB"; payload: string }; // Ajouter l'action SET_ACTIVE_TAB

// Formatter les nombres pour l'affichage
const formatNumber = (value: any): string => {
  if (value === undefined || value === null) return "0";
  // Arrondir à l'entier et ajouter un espace comme séparateur de milliers
  return Math.round(parseFloat(value))
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const formatQuantity = (value: any): string => {
  const numericValue = toNumberWithDecimalSupport(value);
  if (!Number.isFinite(numericValue)) return "0";
  return numericValue.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: QUANTITY_DECIMAL_PLACES,
  });
};

const toNumberWithDecimalSupport = (value: any): number => {
  if (value === null || value === undefined || value === "") return 0;
  const normalized =
    typeof value === "string"
      ? value.replace(/\s/g, "").replace(/,/g, ".")
      : value;
  const numericValue = Number(normalized);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const sanitizeDecimalInput = (
  value: any,
  maxDecimals: number = QUANTITY_DECIMAL_PLACES
): string => {
  if (value === null || value === undefined) return "";

  const normalized = String(value)
    .replace(/\s/g, "")
    .replace(/,/g, ".")
    .replace(/[^0-9.]/g, "");

  if (!normalized) return "";

  const parts = normalized.split(".");
  const integerPart = parts[0] || "0";
  const decimalPart = parts.slice(1).join("").slice(0, maxDecimals);

  return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
};

const normalizePositiveQuantity = (
  value: any,
  fallback: number = MIN_RETURN_QUANTITY
): number => {
  const numericValue = toNumberWithDecimalSupport(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return fallback;
  }

  return Number(numericValue.toFixed(QUANTITY_DECIMAL_PLACES));
};

// Classe API pour les retours d'achat
class RetourAchatAPI {
  retourAchats: RetourAchat[] = [];

  /**
   * Récupère tous les retours d'achat
   */
  async fetchRetourAchats(params: any = {}) {
    try {
      // Adapter les paramètres pour les rendre compatibles avec l'API backend
      const requestParams = { ...params };

      // Transformation des filtres pour la recherche par numéro et la plage de dates
      if (requestParams.search) {
        requestParams.invoice_number = requestParams.search;
        delete requestParams.search;
      }
      if (requestParams.dateDebut) {
        requestParams.date_from = requestParams.dateDebut;
        delete requestParams.dateDebut;
      }
      if (requestParams.dateFin) {
        requestParams.date_to = requestParams.dateFin;
        delete requestParams.dateFin;
      }

      // Pour la compatibilité avec les routes/orders.js, on peut loguer les autres filtres
      if (params.warehouse) {
      }

      if (params.order_status) {
      }

      if (params.payment_status) {
      }

      const response = await axios.get("/api/orders", {
        params: {
          ...requestParams,
          order_type: "purchase_return",
        },
      });

      // Mise à jour de la propriété retourAchats dans l'instance de l'API
      this.retourAchats = response.data.orders || [];

      return {
        retourAchats: response.data.orders || [],
        totalItems:
          response.data.total || response.data.pagination?.totalItems || 0,
      };
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des retours d'achat:",
        error
      );
      throw error;
    }
  }

  /**
   * Récupère la liste des fournisseurs
   */
  async fetchFournisseurs(params: any = {}) {
    try {
      // S'assurer que tous les paramètres sont définis
      const requestParams = {
        user_type: "suppliers",
        status: "enabled",
        ...params,
      };

      if (requestParams.warehouse) {
        requestParams.warehouseId = requestParams.warehouse;
        delete requestParams.warehouse;
      }

      // Première tentative: utiliser l'API spécifique aux fournisseurs
      try {
        const response = await axios.get("/api/users/suppliers", {
          params: requestParams,
        });
        return Array.isArray(response.data) ? response.data : [];
      } catch (specificApiError) {
        console.warn(
          "L'API spécifique aux fournisseurs a échoué, tentative avec l'API générique",
          specificApiError
        );
      }

      // Deuxième tentative: utiliser l'API générique des utilisateurs

      const response = await axios.get("/api/users", {
        params: requestParams,
      });

      // Vérifier la structure des données retournées et les normaliser
      let fournisseursData = [];
      if (
        response.data &&
        response.data.users &&
        Array.isArray(response.data.users)
      ) {
        fournisseursData = response.data.users;
      } else if (Array.isArray(response.data)) {
        fournisseursData = response.data;
      }

      if (fournisseursData.length > 0) {
      }

      return fournisseursData;
    } catch (error) {
      console.error("Erreur lors de la récupération des fournisseurs:", error);

      if (axios.isAxiosError(error) && error.response) {
        console.error(
          `Status: ${error.response.status}, Message: ${error.response.statusText}`
        );
      }

      return [];
    }
  }

  /**
   * Récupère la liste des produits
   */
  async fetchProduits(params: any = {}) {
    try {
      // Si warehouseId est fourni, utiliser warehouse_id pour la compatibilité avec l'API
      const requestParams = { ...params };
      if (params.warehouseId) {
        requestParams.warehouse_id = params.warehouseId;
        delete requestParams.warehouseId;
      }

      const response = await axios.get("/api/produits", {
        params: requestParams,
      });
      return response.data.products;
    } catch (error) {
      console.error("Erreur lors de la récupération des produits:", error);
      throw error;
    }
  }

  /**
   * Récupère la liste des taxes
   */
  async fetchTaxes() {
    try {
      const response = await axios.get("/api/taxes");
      return response.data.taxes;
    } catch (error) {
      console.error("Erreur lors de la récupération des taxes:", error);
      throw error;
    }
  }

  /**
   * Crée un nouveau retour d'achat
   */
  async createRetourAchat(data: any) {
    try {
      const response = await axios.post("/api/orders", {
        ...data,
        order_type: "purchase_return",
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création du retour d'achat:", error);
      throw error;
    }
  }

  /**
   * Met à jour un retour d'achat existant
   */
  async updateRetourAchat(id: number, data: any) {
    try {
      const response = await axios.put(`/api/orders/${id}`, {
        ...data,
        order_type: "purchase_return",
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour du retour d'achat:", error);
      throw error;
    }
  }

  /**
   * Supprime un retour d'achat
   */
  async deleteRetourAchat(id: number) {
    try {
      const response = await axios.delete(`/api/orders/${id}`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la suppression du retour d'achat:", error);
      throw error;
    }
  }

  /**
   * Annule un retour d'achat
   */
  async cancelRetourAchat(id: number) {
    try {
      const payload = { order_status: "Annulé", cancelled: 1, is_deletable: 0 };
      const response = await axios.put(`/api/orders/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'annulation du retour d'achat:", error);
      throw error;
    }
  }

  /**
   * Récupère les détails d'un retour d'achat
   */
  async fetchRetourAchatDetail(id: string) {
    try {
      const response = await axios.get(`/api/orders/${id}`);
      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des détails du retour d'achat:",
        error
      );
      throw error;
    }
  }

  /**
   * Récupère les articles d'un retour d'achat
   */
  async fetchOrderItems(orderId: string) {
    try {
      const response = await axios.get(`/api/orders/${orderId}/items`);
      if (!response.data) {
        return [];
      }

      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des articles de retour d'achat:",
        error
      );
      throw error;
    }
  }

  /**
   * Récupère les commandes d'achat (pour sélectionner une commande d'origine pour le retour)
   */
  async fetchAchatsForReturn(fournisseurId: number, warehouseId: number) {
    try {
      const response = await axios.get("/api/orders", {
        params: {
          user_id: fournisseurId,
          warehouse: warehouseId,
          order_type: "purchase",
        },
      });
      return response.data.orders;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des achats pour retour:",
        error
      );
      throw error;
    }
  }

  /**
   * Restaure un retour d'achat supprimé
   */
  async restoreRetourAchat(id: number) {
    try {
      const response = await axios.post(`/api/orders/${id}/restore`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la restauration du retour d'achat:", error);
      throw error;
    }
  }

  /**
   * Récupère la liste des entrepôts
   */
  async fetchWarehouses() {
    try {
      const response = await axios.get("/api/warehouses", {
        params: { is_active: true },
      });
      return response.data.warehouses;
    } catch (error) {
      console.error("Erreur lors de la récupération des établissements:", error);
      throw error;
    }
  }

  /**
   * Récupère les détails d'un entrepôt
   */
  async fetchWarehouse(id: number) {
    if (!id) {
      console.error("ID d'établissement invalide (null ou undefined)");
      return null;
    }

    try {
      // Vérifier si l'ID est valide
      if (isNaN(id)) {
        console.error("ID d'établissement n'est pas un nombre valide:", id);
        return null;
      }

      try {
        // Première tentative: récupération directe par ID
        const response = await axios.get(`/api/warehouses/${id}`);
        return response.data;
      } catch (directError) {
        console.warn(
          `Erreur lors de la récupération directe de l'établissement ${id}:`,
          directError
        );

        // Si la récupération directe échoue, tenter une récupération par la liste complète
        const allWarehousesResponse = await axios.get("/api/warehouses");

        if (
          allWarehousesResponse.data &&
          allWarehousesResponse.data.warehouses
        ) {
          const warehouses = allWarehousesResponse.data.warehouses;
          const warehouse = warehouses.find(
            (w: any) => Number(w.id) === Number(id)
          );

          if (warehouse) {
            return warehouse;
          }
        }

        console.error(`Établissement ${id} non trouvé dans la liste des établissements`);

        // En dernier recours, retourner un objet minimal avec l'ID
        return {
          id: id,
          name: `Établissement ${id}`,
          address: "",
          phone: "",
          email: "",
        };
      }
    } catch (error) {
      console.error(
        `Erreur lors de la récupération de l'établissement ${id}:`,
        error
      );

      // Retourner un objet minimal en cas d'erreur plutôt que de propager l'erreur
      return {
        id: id,
        name: `Établissement ${id}`,
        address: "",
        phone: "",
        email: "",
      };
    }
  }
}

// Instance de l'API.
// Exportée pour être réutilisée par l'écran mobile (src/mobile/achats/RetoursAchatMobile.jsx).
// Attention : `retourAchatAPI.retourAchats` est un champ mutable que la Table desktop lit
// directement ; le mobile n'utilise que les valeurs de retour des méthodes.
export const retourAchatAPI = new RetourAchatAPI();

/**
 * Reducer pour gérer l'état de l'application
 */
const retourAchatsReducer = (
  state: RetourAchatsState,
  action: RetourAchatsAction
): RetourAchatsState => {
  switch (action.type) {
    case "FETCH_RETOUR_ACHATS_SUCCESS":
      return {
        ...state,
        retourAchats: action.payload,
        totalItems: action.totalItems,
        loading: false,
      };
    case "FETCH_FOURNISSEURS_SUCCESS":
      return {
        ...state,
        fournisseurs: action.payload,
        loading: false,
      };
    case "FETCH_PRODUITS_SUCCESS":
      return {
        ...state,
        produits: action.payload,
        loading: false,
      };
    case "FETCH_TAXES_SUCCESS":
      return {
        ...state,
        taxes: action.payload,
        loading: false,
      };
    case "FETCH_WAREHOUSES_SUCCESS":
      return {
        ...state,
        warehouses: action.payload,
        loading: false,
      };
    case "SET_CURRENT_RETOUR_ACHAT":
      return {
        ...state,
        currentRetourAchat: action.payload,
      };
    case "SET_RETOUR_ACHAT_ITEMS":
      return {
        ...state,
        retourAchatItems: action.payload,
      };
    case "SET_ORDER_PAYMENTS":
      return {
        ...state,
        orderPayments: action.payload,
      };
    case "SET_LOADING":
      return {
        ...state,
        loading: action.payload,
      };
    case "SET_ERROR":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "SET_CURRENT_PAGE":
      return {
        ...state,
        currentPage: action.payload,
      };
    case "SET_CURRENT_WAREHOUSE":
      return {
        ...state,
        currentWarehouse: action.payload,
      };
    case "SET_ACTIVE_TAB": // Ajouter le cas pour SET_ACTIVE_TAB
      return {
        ...state,
        activeTab: action.payload,
      };
    default:
      return state;
  }
};

export const PaymentFormModal = ReturnPurchasePaymentModal;
const OrderDetailModal = ReturnPurchaseDetailModal;

// Composant du formulaire de retour d'achat
interface RetourAchatFormProps {
  visible: boolean;
  onClose: () => void;
  refreshRetourAchats: () => void;
  retourAchatToEdit?: RetourAchat | null; // Ajouter cette propriété pour la modification
  isEditing?: boolean; // Ajouter un flag pour indiquer si on est en mode édition
}

const RetourAchatForm: React.FC<RetourAchatFormProps> = ({
  visible,
  onClose,
  refreshRetourAchats,
  retourAchatToEdit,
  isEditing,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<Fournisseur[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<number | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<RetourAchatItem[]>(
    []
  );
  const [originalOrders, setOriginalOrders] = useState<any[]>([]);
  const [selectedOriginalOrder, setSelectedOriginalOrder] = useState<
    number | null
  >(null);
  const [originalOrderItems, setOriginalOrderItems] = useState<any[]>([]);
  const [subTotal, setSubTotal] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const { selectedWarehouse, selectedCompany } = useSelection(); // Récupérer le magasin sélectionné depuis le contexte
  const { hasPermission } = useAuth();
  const canCreateInitialPayment = hasPermission(
    "Gestion Commerciale.Ventes.PaiementsEntrants.create"
  );
  const [selectedTax, setSelectedTax] = useState<Tax | null>(null);
  const [warehouseContext, setWarehouseContext] = useState<any>(null);
  const warehouseRequestRef = useRef(0);
  const termsValue = Form.useWatch("terms_condition", form);
  const remarksValue = Form.useWatch("remarks", form);
  const warehouseContextLabel =
    String(
      warehouseContext?.name ||
        warehouseContext?.warehouse_name ||
        (typeof selectedWarehouse === "object"
          ? (selectedWarehouse as any)?.name
          : "") ||
        "",
    ).trim() ||
    (selectedWarehouse
      ? `Établissement #${
          typeof selectedWarehouse === "object"
            ? (selectedWarehouse as any)?.id
            : selectedWarehouse
        }`
      : "Aucun établissement sélectionné");
  const additionalInformationSummary =
    termsValue || remarksValue
      ? [
          termsValue ? "Termes renseignés" : null,
          remarksValue ? "Remarques renseignées" : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : "Termes, conditions et remarques";

  // Effet pour initialiser le formulaire avec les données du retour existant si en mode édition
  useEffect(() => {
    if (visible && isEditing && retourAchatToEdit) {
      initFormWithExistingData();
    }
  }, [visible, isEditing, retourAchatToEdit]);

  // Fonction pour initialiser le formulaire avec les données existantes
  const initFormWithExistingData = async () => {
    if (!retourAchatToEdit) return;

    try {
      setLoading(true);

      // Mettre à jour l'état local avec les données du retour d'achat
      setSelectedSupplier(retourAchatToEdit.user_id);

      // Vérifier si on a un original_order_id et le définir
      const originalOrderId = retourAchatToEdit.original_order_id || null;
      setSelectedOriginalOrder(originalOrderId);

      // Rechercher la taxe correspondante si retourAchatToEdit a un tax_id
      if (retourAchatToEdit.tax_id && taxes.length > 0) {
        const tax = taxes.find((t) => t.id === retourAchatToEdit.tax_id);
        if (tax) {
          setSelectedTax(tax);
        }
      }

      // Récupérer les articles du retour si ce n'est pas déjà fait
      let itemsToUse = retourAchatToEdit.items || [];
      if (itemsToUse.length === 0) {
        const items = await retourAchatAPI.fetchOrderItems(
          String(retourAchatToEdit.id)
        );
        itemsToUse = items;
      }

      // Récupérer les articles de la commande d'origine pour avoir les quantités maximales disponibles
      let originalItems: any[] = [];
      if (originalOrderId) {
        try {
          const response = await axios.get(
            `/api/orders/${originalOrderId}/items`
          );
          if (response.data && Array.isArray(response.data)) {
            originalItems = response.data;
          }
        } catch (error) {
          console.error(
            "Erreur lors de la récupération des articles de la commande d'origine:",
            error
          );
        }
      }

      // Préparer les articles pour l'état local
      const formattedItems = itemsToUse.map((item: any) => {
        // Trouver l'article correspondant dans la commande d'origine
        const originalItem = originalItems.find(
          (origItem) =>
            origItem.id === item.original_order_item_id ||
            origItem.product_id === item.product_id
        );

        // Déterminer la quantité maximale (si on a l'article d'origine, utiliser sa quantité, sinon utiliser la quantité actuelle)
        const maxQuantity = originalItem
          ? normalizePositiveQuantity(originalItem.quantity, 0)
          : normalizePositiveQuantity(item.quantity, 0);

        return {
          id: item.id,
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: normalizePositiveQuantity(item.quantity, 0),
          max_quantity: maxQuantity, // Utiliser la quantité de la commande d'origine comme max
          unit_price: Number(item.unit_price),
          discount_rate: Number(item.discount_rate || 0),
          tax_rate: Number(item.tax_rate || 0),
          tax_amount: Number(item.tax_amount || 0),
          subtotal: Number(item.subtotal),
          original_order_id: retourAchatToEdit.original_order_id,
          original_order_item_id: item.original_order_item_id,
        };
      });

      setSelectedProducts(formattedItems);

      // Définir les valeurs du formulaire
      form.setFieldsValue({
        supplier_id: retourAchatToEdit.user_id,
        order_date: dayjs(retourAchatToEdit.order_date),
        order_status: retourAchatToEdit.order_status,
        original_order_id: retourAchatToEdit.original_order_id,
        tax_id: retourAchatToEdit.tax_id,
        terms_condition:
          retourAchatToEdit.terms_condition || retourAchatToEdit.notes || "",
        remarks: retourAchatToEdit.notes || "",
      });

      // Charger les commandes d'origine du fournisseur sélectionné
      if (retourAchatToEdit.user_id && selectedWarehouse) {
        try {
          const orders = await retourAchatAPI.fetchAchatsForReturn(
            retourAchatToEdit.user_id,
            Number(selectedWarehouse)
          );
          setOriginalOrders(orders || []);

          // Vérifier si la commande d'origine n'est pas dans la liste, tenter de la récupérer directement
          if (
            originalOrderId &&
            !orders.some((order: any) => order.id === originalOrderId)
          ) {
            try {
              const originalOrderDetails = await axios.get(
                `/api/orders/${originalOrderId}`
              );
              if (originalOrderDetails.data) {
                // Ajouter la commande d'origine à la liste
                setOriginalOrders([...orders, originalOrderDetails.data]);
              }
            } catch (orderError) {
              console.error(
                "Erreur lors de la récupération de la commande d'origine:",
                orderError
              );
            }
          }

          // Si la commande d'origine est définie, utiliser les articles déjà récupérés
          if (originalOrderId && originalItems.length > 0) {
            setOriginalOrderItems(originalItems);
          }
        } catch (error) {
          console.error(
            "Erreur lors du chargement des commandes d'origine:",
            error
          );
        }
      }
    } catch (error) {
      console.error(
        "Erreur lors de l'initialisation du formulaire avec les données existantes:",
        error
      );
      message.error("Erreur lors du chargement des données du retour d'achat");
    } finally {
      setLoading(false);
    }
  };

  // Log pour le débogage
  useEffect(() => {}, [selectedWarehouse]);

  // Vérifier la valeur de selectedWarehouse quand le composant est monté
  useEffect(() => {
    if (selectedWarehouse) {
    } else {
      console.warn("AUCUN ÉTABLISSEMENT SÉLECTIONNÉ!");
    }
  }, [selectedWarehouse]);

  // Charger les termes et conditions de l'entrepôt sélectionné
  const loadWarehouseTerms = async (warehouseId: number) => {
    if (!warehouseId) {
      console.error("ID d'établissement non fourni à loadWarehouseTerms");
      return;
    }

    const requestId = ++warehouseRequestRef.current;
    try {
      // Utiliser la méthode fetchWarehouse améliorée
      const warehouse = await retourAchatAPI.fetchWarehouse(warehouseId);
      if (requestId !== warehouseRequestRef.current) return;
      setWarehouseContext(warehouse || null);

      if (warehouse) {
        if (warehouse.terms_condition) {
          form.setFieldsValue({ terms_condition: warehouse.terms_condition });
        } else {
          form.setFieldsValue({ terms_condition: "" });
        }
      } else {
        console.warn(
          `Aucune donnée d'établissement récupérée pour l'ID ${warehouseId}`
        );
        form.setFieldsValue({ terms_condition: "" });
      }
    } catch (error) {
      console.error(
        `Erreur lors du chargement des termes et conditions:`,
        error
      );
      if (requestId === warehouseRequestRef.current) {
        form.setFieldsValue({ terms_condition: "" });
        setWarehouseContext(null);
      }
    }
  };

  // Réinitialiser le formulaire - définir AVANT useEffect qui l'utilise
  const resetForm = () => {
    form.resetFields();
    setSelectedProducts([]);
    setSelectedSupplier(null);
    setSelectedOriginalOrder(null);
    setOriginalOrderItems([]);
    setSubTotal(0);
    setDiscountAmount(0);
    setTaxAmount(0);
    setGrandTotal(0);
    warehouseRequestRef.current += 1;
    setWarehouseContext(null);
  };

  // Charger les données initiales
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Convertir selectedWarehouse en nombre pour s'assurer de la compatibilité
        const warehouseId = selectedWarehouse
          ? Number(selectedWarehouse)
          : null;

        if (!warehouseId) {
          message.warning("Veuillez sélectionner un établissement d'abord");
          setLoading(false);
          return;
        }

        // Utiliser l'API pour charger les fournisseurs associés au magasin sélectionné
        const suppliersRes = await retourAchatAPI.fetchFournisseurs({
          warehouse: warehouseId,
        });

        if (Array.isArray(suppliersRes)) {
          setSuppliers(suppliersRes);
        } else {
          console.error(
            "Unexpected response format from fetchFournisseurs:",
            suppliersRes
          );
          setSuppliers([]);
        }

        // Charger les taxes
        try {
          const taxesRes = await retourAchatAPI.fetchTaxes();
          setTaxes(taxesRes || []);
        } catch (taxesError) {
          console.error("Erreur lors du chargement des taxes:", taxesError);
          setTaxes([]);
        }

        // Utiliser le magasin du contexte pour charger les termes et conditions
        await loadWarehouseTerms(warehouseId);
      } catch (error) {
        console.error("Error loading form data:", error);
        message.error("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };

    if (visible) {
      loadData();
    } else {
      resetForm();
    }
  }, [visible, selectedWarehouse]);

  // Fermer le formulaire
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Charger les commandes d'achat originales quand le fournisseur change
  useEffect(() => {
    const loadOriginalOrders = async () => {
      if (!selectedSupplier) {
        setOriginalOrders([]); // Réinitialiser les commandes si aucun fournisseur n'est sélectionné
        return;
      }

      try {
        setLoading(true);
        // Convertir les IDs en nombres
        const supplierId = Number(selectedSupplier);
        const warehouseId = selectedWarehouse
          ? Number(selectedWarehouse)
          : null;

        if (!warehouseId) {
          message.warning("Veuillez sélectionner un établissement d'abord");
          setLoading(false);
          return;
        }

        const orders = await retourAchatAPI.fetchAchatsForReturn(
          supplierId,
          warehouseId
        );

        setOriginalOrders(orders || []);
      } catch (fetchError) {
        console.error("Error loading original orders:", fetchError);
        message.error("Erreur lors du chargement des achats originaux");
        setOriginalOrders([]); // Réinitialiser en cas d'erreur
      } finally {
        setLoading(false);
      }
    };

    loadOriginalOrders();
  }, [selectedSupplier, selectedWarehouse]);

  // Enregistrer le retour d'achat
  const onFinish = async (values: any) => {
    if (selectedProducts.length === 0) {
      message.error("Veuillez ajouter au moins un produit au retour d'achat");
      return;
    }

    setLoading(true);

    try {
      // Récupérer l'ID de l'entreprise à partir du contexte
      const companyId = selectedCompany;

      // Calculer le nombre total d'articles et de quantités
      const totalItems = selectedProducts.length;
      const totalQuantity = selectedProducts.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
      const initialPayment = isEditing
        ? undefined
        : buildInitialPaymentPayload(values, {
            paymentType: "in",
            defaultNotes:
              "Remboursement enregistré pendant le retour d'achat",
          });

      // Préparer les données pour l'API
      const orderData = {
        company_id: companyId,
        order_type: "purchase_return",
        user_id: values.supplier_id,
        warehouse_id: selectedWarehouse,
        order_date: values.order_date.format("YYYY-MM-DD"),
        order_status: values.order_status,
        discount: discountAmount,
        tax_id: selectedTax ? selectedTax.id : null,
        tax_rate: selectedTax ? selectedTax.rate : null,
        tax_amount: taxAmount,
        subtotal: subTotal,
        total: grandTotal,
        paid_amount: isEditing ? retourAchatToEdit?.paid_amount || 0 : 0,
        due_amount: isEditing
          ? grandTotal - (retourAchatToEdit?.paid_amount || 0)
          : grandTotal,
        payment_status: isEditing
          ? calculatePaymentStatus(
              retourAchatToEdit?.paid_amount || 0,
              grandTotal
            )
          : "Non payé",
        initial_payment: initialPayment,
        terms_condition: values.terms_condition || "",
        notes: values.remarks || "",
        staff_user_id: null,
        total_items: totalItems,
        total_quantity: totalQuantity,
        original_order_id: selectedOriginalOrder,
        items: selectedProducts.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_rate: item.discount_rate,
          tax_rate: selectedTax ? selectedTax.rate : item.tax_rate,
          tax_amount: selectedTax
            ? (item.unit_price *
                item.quantity *
                (1 - item.discount_rate / 100) *
                selectedTax.rate) /
              100
            : item.tax_amount,
          subtotal: item.subtotal,
          original_order_item_id: item.original_order_item_id,
          id: isEditing && item.id ? item.id : undefined, // Inclure l'ID de l'item si disponible en mode édition
        })),
      };

      // Si on est en mode édition, récupérer d'abord les détails du retour existant
      // pour calculer les ajustements de stock
      if (isEditing && retourAchatToEdit) {
        // Récupérer les articles actuels du retour d'achat si ce n'est pas déjà fait
        let currentItems = retourAchatToEdit.items || [];
        if (currentItems.length === 0) {
          try {
            currentItems = await retourAchatAPI.fetchOrderItems(
              String(retourAchatToEdit.id)
            );
          } catch (error) {
            console.error(
              "Erreur lors de la récupération des articles actuels:",
              error
            );
          }
        }

        // Ajouter une note dans le champ notes pour indiquer qu'il s'agit d'une modification
        orderData.notes = `${orderData.notes || ""} - `;

        // Préparer un tableau d'ajustements de stock pour le logging (mais ne pas l'ajouter à orderData)
        const stockAdjustments = selectedProducts.map((item) => {
          // Trouver l'article correspondant dans les articles actuels
          const currentItem = currentItems.find(
            (ci: any) => ci.id === item.id || ci.product_id === item.product_id
          );

          // Calculer la différence de quantité (nouvelle quantité - ancienne quantité)
          const quantityDiff = currentItem
            ? item.quantity - Number(currentItem.quantity)
            : item.quantity;

          return {
            product_id: item.product_id,
            product_name: item.product_name,
            quantity_diff: quantityDiff,
            previous_quantity: currentItem ? Number(currentItem.quantity) : 0,
            new_quantity: item.quantity,
          };
        });

        // Logger les ajustements de stock pour référence

        // Appeler l'API pour mettre à jour le retour d'achat
        await retourAchatAPI.updateRetourAchat(retourAchatToEdit.id, orderData);
        message.success("Retour d'achat mis à jour avec succès");
      } else {
        // Création d'un nouveau retour d'achat
        await retourAchatAPI.createRetourAchat(orderData);
        message.success("Retour d'achat enregistré avec succès");
      }

      resetForm();
      refreshRetourAchats();
      onClose();
    } catch (error) {
      console.error(
        "Erreur lors de l'enregistrement du retour d'achat:",
        error
      );
      const responseData = (error as any)?.response?.data;
      message.error(
        responseData?.details ||
          responseData?.error ||
          (error instanceof Error && error.message) ||
          "Erreur lors de l'enregistrement du retour d'achat"
      );
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour calculer le statut de paiement
  const calculatePaymentStatus = (
    paidAmount: number,
    totalAmount: number
  ): string => {
    if (paidAmount <= 0) {
      return "Non payé";
    } else if (paidAmount < totalAmount) {
      return "Partiellement payé";
    } else {
      return "Payé";
    }
  };

  // Mettre à jour un produit de la liste
  const handleUpdateProduct = (index: number, key: string, value: any) => {
    const updatedProducts = [...selectedProducts];
    const product = { ...updatedProducts[index] };

    // Mettre à jour la valeur
    if (key === "quantity") {
      (product as any)[key] = normalizePositiveQuantity(value);
    } else {
      (product as any)[key] = toNumberWithDecimalSupport(value);
    }

    // Recalculer les valeurs financières
    const quantity = product.quantity;
    const unitPrice = product.unit_price;
    const discountRate = product.discount_rate;
    const discountAmount = (unitPrice * quantity * discountRate) / 100;
    const taxableAmount = unitPrice * quantity - discountAmount;
    const taxRate = product.tax_rate;
    const taxAmount = (taxableAmount * taxRate) / 100;
    const subtotal = taxableAmount + taxAmount;

    product.tax_amount = taxAmount;
    product.subtotal = subtotal;

    updatedProducts[index] = product;
    setSelectedProducts(updatedProducts);
  };

  // Supprimer un produit de la liste
  const handleRemoveProduct = (index: number) => {
    const updatedProducts = [...selectedProducts];
    const removedProduct = updatedProducts[index];

    // Si le produit provient d'une commande d'origine, mettre à jour la quantité retournable
    if (removedProduct.original_order_item_id) {
      const updatedOriginalItems = originalOrderItems.map((item) => {
        if (item.id === removedProduct.original_order_item_id) {
          return {
            ...item,
            return_quantity: 0,
          };
        }
        return item;
      });
      setOriginalOrderItems(updatedOriginalItems);
    }

    updatedProducts.splice(index, 1);
    setSelectedProducts(updatedProducts);
  };

  useEffect(() => {
    const loadOriginalOrderItems = async () => {
      if (!selectedOriginalOrder) {
        setSelectedProducts([]);
        return;
      }

      try {
        // Utiliser l'endpoint correct du fichier routes/orders.js
        const response = await axios.get(
          `/api/orders/${selectedOriginalOrder}/items`
        );

        if (!response.data || !Array.isArray(response.data)) {
          message.error("Impossible de charger les articles de la commande");
          return;
        }

        // Transformer directement les items de la commande d'origine en articles sélectionnés pour le retour
        const returnItems: RetourAchatItem[] = response.data.map(
          (item: any) => {
            // Calculer les valeurs financières pour chaque article
            const unitPrice = item.unit_price;
            const quantity = item.quantity;
            const discountRate = item.discount_rate || 0;
            const discountAmount = (unitPrice * quantity * discountRate) / 100;
            const taxableAmount = unitPrice * quantity - discountAmount;
            const taxRate = item.tax_rate || 0;
            const taxAmount = (taxableAmount * taxRate) / 100;
            const subtotal = taxableAmount + taxAmount;

            return {
              product_id: item.product_id,
              product_name: item.product_name || item.nom_produit, // Prend en compte les deux possibilités
              quantity: item.quantity,
              max_quantity: item.quantity, // Stocker la quantité originale comme maximum
              unit_price: unitPrice,
              discount_rate: discountRate,
              tax_rate: taxRate,
              tax_amount: taxAmount,
              subtotal,
              original_order_id: selectedOriginalOrder,
              original_order_item_id: item.id,
            };
          }
        );

        setSelectedProducts(returnItems);
      } catch (error) {
        console.error(
          "Erreur lors du chargement des articles de la commande:",
          error
        );
        message.error("Erreur lors du chargement des articles de la commande");
      }
    };

    loadOriginalOrderItems();
  }, [selectedOriginalOrder]);

  // Dans le calcul des totaux, supprimer la partie liée aux frais de livraison
  useEffect(() => {
    // Calculer le sous-total (prix total avant taxes)
    const newSubTotal = selectedProducts.reduce((total, item) => {
      const itemPrice = item.unit_price * item.quantity;
      const itemDiscount = (itemPrice * item.discount_rate) / 100;
      return total + (itemPrice - itemDiscount);
    }, 0);
    setSubTotal(newSubTotal);

    // Calculer le total des remises
    const newDiscountAmount = selectedProducts.reduce((total, item) => {
      const itemPrice = item.unit_price * item.quantity;
      const itemDiscount = (itemPrice * item.discount_rate) / 100;
      return total + itemDiscount;
    }, 0);
    setDiscountAmount(newDiscountAmount);

    // Calculer la taxe globale si une taxe est sélectionnée
    let newTaxAmount = 0;
    if (selectedTax) {
      newTaxAmount = (newSubTotal * selectedTax.rate) / 100;

      // Mettre à jour les taxes pour chaque produit
      const updatedProducts = selectedProducts.map((item) => {
        const itemPrice = item.unit_price * item.quantity;
        const itemDiscount = (itemPrice * item.discount_rate) / 100;
        const taxableAmount = itemPrice - itemDiscount;
        const itemTaxAmount = (taxableAmount * selectedTax.rate) / 100;

        return {
          ...item,
          tax_rate: selectedTax.rate,
          tax_amount: itemTaxAmount,
          subtotal: taxableAmount + itemTaxAmount,
        };
      });

      if (
        JSON.stringify(updatedProducts) !== JSON.stringify(selectedProducts)
      ) {
        setSelectedProducts(updatedProducts);
      }
    } else {
      // Sinon, utiliser les taxes individuelles des produits
      newTaxAmount = selectedProducts.reduce(
        (total, item) => total + item.tax_amount,
        0
      );
    }
    setTaxAmount(newTaxAmount);

    // Calculer le total
    const newGrandTotal = newSubTotal + newTaxAmount;
    setGrandTotal(newGrandTotal);
  }, [selectedProducts, selectedTax]);

  // Ajout de useEffect pour charger les commandes d'origine en mode création
  useEffect(() => {
    if (!isEditing) {
      const loadOriginalOrdersForSupplier = async () => {
        if (!selectedSupplier || !selectedWarehouse) {
          setOriginalOrders([]);
          return;
        }
        try {
          setLoading(true);
          const orders = await retourAchatAPI.fetchAchatsForReturn(
            selectedSupplier,
            Number(selectedWarehouse)
          );
          setOriginalOrders(orders || []);
        } catch (error) {
          console.error(
            "Erreur lors du chargement des commandes d'origine:",
            error
          );
          message.error("Erreur lors du chargement des commandes d'origine");
        } finally {
          setLoading(false);
        }
      };
      loadOriginalOrdersForSupplier();
    }
  }, [selectedSupplier, selectedWarehouse, isEditing]);

  return (
    <Modal
      className="sale-form-modal"
      title={
        <CommercialDocumentModalTitle
          kind="purchase-return"
          title={
            isEditing
              ? "Modifier le retour d'achat"
              : "Nouveau retour d'achat"
          }
          subtitle="Sélectionner l’achat d’origine, contrôler les quantités et enregistrer le retour"
          contextLabel={warehouseContextLabel}
        />
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={window.innerWidth > 768 ? "96%" : "100%"}
      style={{ maxWidth: "1760px" }}
      bodyStyle={{
        maxHeight: "calc(100vh - 108px)",
        overflowY: "auto",
        padding: 0,
      }}
      destroyOnClose
    >
      <div className="sale-form-modal__content">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="sale-form"
        initialValues={{
          order_date: dayjs(),
          order_status: "completed",
        }}
      >
        <div className="sale-form__layout">
          <main className="sale-form__work">
        <Row gutter={[12, 8]} className="sale-form__setup">
          <Col xs={24} md={12}>
            <Form.Item
              name="supplier_id"
              label="Fournisseur"
              rules={[
                {
                  required: true,
                  message: "Veuillez sélectionner un fournisseur",
                },
              ]}
            >
              <Select
                placeholder="Sélectionner un fournisseur"
                showSearch
                optionFilterProp="searchLabel"
                filterOption={(input, option: any) =>
                  matchesCommercialEntitySearch(option?.searchLabel, input)
                }
                onChange={(value) => {
                  setSelectedSupplier(value as number);
                  setSelectedOriginalOrder(null);
                }}
              >
                {suppliers.map((supplier) => (
                  <Option
                    key={supplier.id}
                    value={supplier.id}
                    searchLabel={buildCommercialEntitySearchText(supplier)}
                  >
                    {getCommercialEntityDisplayName(
                      supplier,
                      "Fournisseur inconnu"
                    )}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              name="order_date"
              label="Date du retour"
              rules={[
                { required: true, message: "Veuillez sélectionner une date" },
              ]}
            >
              <DatePicker
                className="full-width"
                format="DD/MM/YYYY"
                allowClear={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              name="order_status"
              label="Statut retour d'achat"
              rules={[
                { required: true, message: "Veuillez sélectionner un statut" },
              ]}
            >
              <Select placeholder="Sélectionner un statut">
                <Option value="pending">En attente</Option>
                <Option value="completed">Complété</Option>
                <Option value="cancelled">Annulé</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[12, 8]} className="sale-form__setup">
          <Col span={24}>
            <Form.Item
              label="Commande d'origine"
              name="original_order_id"
              rules={[
                {
                  required: true,
                  message: "Veuillez sélectionner une commande d'origine",
                },
              ]}
            >
              <Select
                placeholder={
                  selectedSupplier
                    ? "Sélectionner une commande d'achat"
                    : "Veuillez d'abord sélectionner un fournisseur"
                }
                onChange={(value) => setSelectedOriginalOrder(value)}
                disabled={!selectedSupplier}
              >
                {originalOrders.map((order) => (
                  <Option key={order.id} value={order.id}>
                    {order.invoice_number} -{" "}
                    {dayjs(order.order_date).format("DD/MM/YYYY")} -{" "}
                    {formatNumber(order.total)} FCFA
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[12, 8]} className="sale-form__setup">
          <Col span={24}>
            <Form.Item label="Taxe globale" name="tax_id">
              <Select
                placeholder="Sélectionner une taxe (optionnel)"
                allowClear
                onChange={(value) => {
                  if (value) {
                    const tax = taxes.find((t) => t.id === value);
                    setSelectedTax(tax || null);
                  } else {
                    setSelectedTax(null);
                  }
                }}
              >
                {taxes.map((tax) => (
                  <Option key={tax.id} value={tax.id}>
                    {tax.name} ({tax.rate}%)
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="commercial-document-section-heading">
          <strong>Articles sélectionnés pour le retour</strong>
          <span>
            {selectedProducts.length} article{selectedProducts.length > 1 ? "s" : ""}
          </span>
        </div>

        {selectedOriginalOrder || (isEditing && selectedProducts.length > 0) ? (
          <div className="sale-form-table-shell">
            <Table
              className="sale-form-table"
              dataSource={selectedProducts}
              rowKey={(record) =>
                `${record.product_id}_${
                  record.original_order_item_id ||
                  Math.random().toString(36).substr(2, 9)
                }`
              }
              pagination={false}
              columns={[
                {
                  title: "Produit",
                  dataIndex: "product_name",
                  key: "product_name",
                },
                {
                  title: "Quantité",
                  dataIndex: "quantity",
                  key: "quantity",
                  width: 120,
                  render: (text, record, index) => (
                    <InputNumber
                      min={MIN_RETURN_QUANTITY}
                      step={0.00001}
                      controls={false}
                      max={record.max_quantity} // Limiter au maximum de la quantité originale
                      value={text}
                      formatter={(displayValue: any, info) =>
                        info.userTyping
                          ? info.input
                          : displayValue === undefined || displayValue === null
                            ? ""
                            : formatQuantity(displayValue)
                      }
                      parser={(value) =>
                        sanitizeDecimalInput(value, QUANTITY_DECIMAL_PLACES)
                      }
                      onChange={(value) => {
                        const normalizedValue = normalizePositiveQuantity(value);
                        // Ne pas permettre la saisie manuelle au-delà de la quantité max
                        if (
                          normalizedValue &&
                          normalizedValue > (record.max_quantity || 0)
                        ) {
                          message.warning(
                            `La quantité ne peut pas dépasser la quantité originale (${record.max_quantity}).`
                          );
                          handleUpdateProduct(
                            index,
                            "quantity",
                            record.max_quantity
                          );
                        } else {
                          handleUpdateProduct(
                            index,
                            "quantity",
                            normalizedValue
                          );
                        }
                      }}
                    />
                  ),
                },
                {
                  title: "Prix unitaire",
                  dataIndex: "unit_price",
                  key: "unit_price",
                  width: 150,
                  render: (text, record, index) => (
                    <InputNumber
                      min={0}
                      step={0.01}
                      value={text}
                      onChange={(value) =>
                        handleUpdateProduct(index, "unit_price", value)
                      }
                      formatter={(value) => `${value} FCFA`}
                      parser={(value) => {
                        const parsed = value!.replace(" FCFA", "");
                        return parseFloat(parsed);
                      }}
                    />
                  ),
                },
                {
                  title: "Remise (%)",
                  dataIndex: "discount_rate",
                  key: "discount_rate",
                  width: 120,
                  render: (text, record, index) => (
                    <InputNumber
                      min={0}
                      max={100}
                      value={text}
                      onChange={(value) =>
                        handleUpdateProduct(index, "discount_rate", value)
                      }
                    />
                  ),
                },
                {
                  title: "Taxe (%)",
                  dataIndex: "tax_rate",
                  key: "tax_rate",
                  width: 180,
                  render: (text, record, index) => {
                    // Si une taxe globale est sélectionnée, afficher juste le texte
                    if (selectedTax !== null) {
                      return `${record.tax_rate}%`;
                    }

                    // Sinon, afficher le select pour choisir une taxe
                    return (
                      <div
                        className="tax-selector"
                        style={{ position: "relative", zIndex: 1 }}
                      >
                        <Select
                          style={{ width: "100%" }}
                          value={record.tax_rate}
                          onChange={(value) => {
                            // Trouver la taxe correspondante (conversion explicite en string puis en float)
                            const selectedTaxRate = parseFloat(String(value));

                            // Mettre à jour directement les produits sélectionnés
                            const newProducts = selectedProducts.map(
                              (item, idx) => {
                                if (idx === index) {
                                  // Recalculer pour cet article
                                  const quantity = item.quantity;
                                  const unitPrice = item.unit_price;
                                  const discountRate = item.discount_rate;
                                  const discountAmount =
                                    (unitPrice * quantity * discountRate) / 100;
                                  const taxableAmount =
                                    unitPrice * quantity - discountAmount;
                                  const taxAmount =
                                    (taxableAmount * selectedTaxRate) / 100;

                                  return {
                                    ...item,
                                    tax_rate: selectedTaxRate,
                                    tax_amount: taxAmount,
                                    subtotal: taxableAmount + taxAmount,
                                  };
                                }
                                return item;
                              }
                            );

                            setSelectedProducts(newProducts);
                          }}
                          getPopupContainer={(triggerNode) =>
                            triggerNode.parentNode as HTMLElement
                          }
                          dropdownStyle={{ zIndex: 9999 }}
                        >
                          {taxes.map((tax) => (
                            <Option key={tax.id} value={tax.rate}>
                              {tax.name} ({tax.rate}%)
                            </Option>
                          ))}
                        </Select>
                      </div>
                    );
                  },
                },
                {
                  title: "Sous-total",
                  dataIndex: "subtotal",
                  key: "subtotal",
                  width: 150,
                  render: (text) => `${formatNumber(text)} FCFA`,
                },
                {
                  title: "Actions",
                  key: "actions",
                  width: 100,
                  render: (_, record, index) => (
                    <Button
                      className="sale-form-line-action"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={`Retirer ${record.product_name} du retour`}
                      onClick={() => handleRemoveProduct(index)}
                    />
                  ),
                },
              ]}
              summary={() => (
                // Ligne de résumé vide, car le sous-total n'est plus pertinent
                <Table.Summary.Row></Table.Summary.Row>
              )}
            />
          </div>
        ) : (
          <div className="empty-order-selection">
            <Alert
              message="Aucune commande d'origine sélectionnée"
              description="Veuillez sélectionner une commande d'achat d'origine pour afficher les articles à retourner."
              type="info"
              showIcon
            />
          </div>
        )}

        <CommercialDocumentAdditionalInfo summary={additionalInformationSummary}>
          <Row gutter={[12, 8]}>
            <Col xs={24} md={12}>
              <Form.Item name="terms_condition" label="Termes et conditions">
                <TextArea
                  rows={3}
                  placeholder="Termes et conditions du retour d'achat"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="remarks" label="Remarques">
                <TextArea rows={3} placeholder="Remarques additionnelles" />
              </Form.Item>
            </Col>
          </Row>
        </CommercialDocumentAdditionalInfo>
          </main>

          <CommercialDocumentSummary
            ariaLabel="Résumé du retour d'achat"
            title="Résumé du retour"
            countLabel={
              selectedProducts.length === 0
                ? "Aucun article à retourner"
                : `${selectedProducts.length} article${
                    selectedProducts.length > 1 ? "s" : ""
                  } à retourner`
            }
            rows={[
              { label: "Base HT", value: `${formatNumber(subTotal)} CFA` },
              { label: "Remises", value: `${formatNumber(discountAmount)} CFA` },
              { label: "Taxes", value: `${formatNumber(taxAmount)} CFA` },
            ]}
            totalLabel="Montant à recevoir"
            totalValue={`${formatNumber(grandTotal)} CFA`}
            actions={
              <>
                <Button onClick={handleClose}>Annuler</Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  loading={loading}
                  disabled={selectedProducts.length === 0}
                >
                  {isEditing
                    ? "Mettre à jour le retour"
                    : "Enregistrer le retour"}
                </Button>
              </>
            }
          >
            {!isEditing ? (
              <OrderInitialPaymentFields
                form={form}
                total={grandTotal}
                companyId={selectedCompany as any}
                warehouseId={selectedWarehouse as any}
                canCreatePayment={canCreateInitialPayment}
                presentation="summary"
                inactiveTitle="Remboursement non reçu"
                sectionTitle="Remboursement fournisseur"
                inactiveOptionLabel="Sans remboursement"
                activeOptionLabel="Ajouter un remboursement"
                description="Remboursement fournisseur partiel ou total, enregistré avec le retour."
              />
            ) : (
              <div className="sale-form-summary__payment-readonly">
                <span>Remboursement</span>
                <Tag color="orange">
                  {retourAchatToEdit?.payment_status || "Géré depuis le détail"}
                </Tag>
              </div>
            )}
          </CommercialDocumentSummary>
        </div>
      </Form>
      </div>
    </Modal>
  );
};

// Composant pour la table des retours d'achat
interface RetourAchatsTableProps {
  refreshRetourAchats: () => void;
  tabKey?: string; // Ajouter la propriété tabKey
}

const RetourAchatsTable: React.FC<RetourAchatsTableProps> = ({
  refreshRetourAchats,
  tabKey = "tous", // Valeur par défaut
}) => {
  const [searchText, setSearchText] = useState("");
  const [filterVisible, setFilterVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRetourAchat, setSelectedRetourAchat] =
    useState<RetourAchat | null>(null);
  const [fournisseurs, setFournisseurs] = useState<Fournisseur[]>([]);
  const [produits, setProduits] = useState<Product[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const retourAchatsRequestRef = useRef(0);
  const [filters, setFilters] = useState({
    status: "",
    user_id: "",
    warehouse_id: "",
    dateRange: [null, null] as [any, any],
  });
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const { selectedWarehouse, selectedCompany } = useSelection(); // Récupérer l'entrepôt sélectionné du contexte
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const [retourAchatToEdit, setRetourAchatToEdit] =
    useState<RetourAchat | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  // Utiliser une approche plus simple avec un type plus précis
  // @ts-ignore - Context from JS file
  const auth = null;

  // Remplacer notre implémentation temporaire par le hook personnalisé
  const checkPermission = usePermission();

  // Charger les données de base
  useEffect(() => {
    const loadData = async () => {
      try {
        const [fournisseursRes, produitsRes, taxesRes, warehousesRes] =
          await Promise.all([
            axios.get("/api/users", {
              params: {
                user_type: "suppliers",
                warehouse_id: selectedWarehouse, // Filtrer par entrepôt sélectionné
              },
            }),
            axios.get("/api/produits", {
              params: {
                warehouse_id: selectedWarehouse, // Filtrer par entrepôt sélectionné
              },
            }),
            axios.get("/api/taxes"),
            axios.get("/api/warehouses"),
          ]);

        setFournisseurs(fournisseursRes.data.users);
        setProduits(produitsRes.data.products);
        setTaxes(taxesRes.data.taxes);
        setWarehouses(warehousesRes.data.warehouses);
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        message.error("Erreur lors du chargement des données");
      }
    };

    loadData();
  }, [selectedWarehouse]); // Recharger les données quand l'entrepôt change

  // Charger les détails d'un retour d'achat
  const loadAchatDetails = async (record: any) => {
    setLoading(true);
    try {
      // Récupération des détails du retour d'achat
      const retourAchatDetails = await retourAchatAPI.fetchRetourAchatDetail(
        record.id
      );

      // Récupération des articles du retour
      const retourAchatItems = await retourAchatAPI.fetchOrderItems(record.id);

      // Vérifier si les informations du fournisseur et de l'entrepôt sont présentes
      let detailsWithSupplierInfo = { ...retourAchatDetails };

      // Si le nom du fournisseur est manquant et que l'ID du fournisseur est disponible
      if (
        !detailsWithSupplierInfo.user_name &&
        detailsWithSupplierInfo.user_id
      ) {
        try {
          // Chercher le fournisseur dans la liste déjà chargée
          const supplier = fournisseurs.find(
            (f) => f.id === detailsWithSupplierInfo.user_id
          );
          if (supplier) {
            detailsWithSupplierInfo.user_name = supplier.name;
          } else {
            // Si pas trouvé, essayer de le récupérer depuis l'API

            try {
              const supplierResponse = await axios.get(
                `/api/users/${detailsWithSupplierInfo.user_id}`
              );
              if (supplierResponse.data && supplierResponse.data.name) {
                detailsWithSupplierInfo.user_name = supplierResponse.data.name;
              }
            } catch (supplierError) {
              console.error(
                "Erreur lors de la récupération du fournisseur:",
                supplierError
              );
            }
          }
        } catch (error) {
          console.error(
            "Erreur lors de la récupération du nom du fournisseur:",
            error
          );
        }
      }

      // Si le nom de l'entrepôt est manquant et que l'ID de l'entrepôt est disponible
      if (
        !detailsWithSupplierInfo.warehouse_name &&
        detailsWithSupplierInfo.warehouse_id
      ) {
        try {
          // Chercher l'entrepôt dans la liste des entrepôts chargés
          const warehouse = warehouses.find(
            (w) => w.id === detailsWithSupplierInfo.warehouse_id
          );
          if (warehouse) {
            detailsWithSupplierInfo.warehouse_name = warehouse.name;
          } else {
            // Si pas trouvé, essayer de le récupérer depuis l'API

            try {
              const warehouseResponse = await axios.get(
                `/api/warehouses/${detailsWithSupplierInfo.warehouse_id}`
              );
              if (warehouseResponse.data && warehouseResponse.data.name) {
                detailsWithSupplierInfo.warehouse_name =
                  warehouseResponse.data.name;
              }
            } catch (warehouseError) {
              console.error(
                "Erreur lors de la récupération de l'établissement:",
                warehouseError
              );
            }
          }
        } catch (error) {
          console.error(
            "Erreur lors de la récupération du nom de l'établissement:",
            error
          );
        }
      }

      // Mise à jour des détails du retour d'achat avec les informations complètes
      setSelectedRetourAchat({
        ...detailsWithSupplierInfo,
        items: retourAchatItems,
      });

      setDetailModalVisible(true);
    } catch (error) {
      console.error(
        "Erreur lors du chargement des détails du retour d'achat:",
        error
      );
      message.error("Erreur lors du chargement des détails du retour d'achat");
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger les articles lorsqu'une ligne est étendue
  const fetchRetourItems = async (record: RetourAchat) => {
    try {
      setLoading(true);

      // Récupérer les articles du retour d'achat
      const items = await retourAchatAPI.fetchOrderItems(String(record.id));

      // Mettre à jour les retours d'achat pour inclure les articles
      const updatedRetourAchats = retourAchatAPI.retourAchats.map((retour) => {
        if (retour.id === record.id) {
          return { ...retour, items };
        }
        return retour;
      });

      // Mettre à jour la liste des retours d'achat dans l'API
      retourAchatAPI.retourAchats = updatedRetourAchats;

      return items;
    } catch (error) {
      console.error(
        `Erreur lors du chargement des articles pour le retour #${record.id}:`,
        error
      );
      message.error("Erreur lors du chargement des articles");
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Supprimer un retour d'achat
  const handleDelete = async (id: number) => {
    try {
      await retourAchatAPI.deleteRetourAchat(id);
      message.success("Retour d'achat supprimé avec succès");
      refreshRetourAchats();
    } catch (error) {
      console.error("Erreur lors de la suppression du retour d'achat:", error);
      message.error("Erreur lors de la suppression du retour d'achat");
    }
  };

  // Annuler un retour d'achat
  const handleCancel = async (id: number) => {
    try {
      await retourAchatAPI.cancelRetourAchat(id);
      message.success("Retour d'achat annulé avec succès");
      refreshRetourAchats();
    } catch (error) {
      console.error("Erreur lors de l'annulation du retour d'achat:", error);
      message.error("Erreur lors de l'annulation du retour d'achat");
    }
  };

  // Restaurer un retour d'achat supprimé
  const handleRestore = async (id: number) => {
    try {
      await retourAchatAPI.restoreRetourAchat(id);
      message.success("Retour d'achat restauré avec succès");
      refreshRetourAchats();
    } catch (error) {
      console.error("Erreur lors de la restauration du retour d'achat:", error);
      message.error("Erreur lors de la restauration du retour d'achat");
    }
  };

  // Charger les retours d'achat avec pagination et filtres
  const loadRetourAchats = async (page = 1, pageSize = pagination.pageSize) => {
    const requestId = ++retourAchatsRequestRef.current;
    setLoading(true);
    try {
      const params: any = {
        page,
        limit: pageSize,
        order_type: "purchase_return",
        search: searchText,
        warehouse: selectedWarehouse, // Changé de warehouse_id à warehouse pour correspondre au backend
      };

      // Ajouter les filtres
      if (filters.status) params.order_status = filters.status;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.warehouse_id) params.warehouse = filters.warehouse_id; // Changé de warehouse_id à warehouse
      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        params.dateDebut = filters.dateRange[0].format("YYYY-MM-DD"); // Changé de date_from à dateDebut
        params.dateFin = filters.dateRange[1].format("YYYY-MM-DD"); // Changé de date_to à dateFin
      }

      // Ajouter le filtre par statut de paiement en fonction de l'onglet actif
      if (tabKey === "unpaid") {
        params.payment_status = "Non payé";
      } else if (tabKey === "partially_paid") {
        params.payment_status = "Partiellement payé";
      } else if (tabKey === "paid") {
        params.payment_status = "Payé";
      }

      const response = await new RetourAchatAPI().fetchRetourAchats(params);
      if (requestId !== retourAchatsRequestRef.current) return;

      // Si le filtrage côté serveur ne fonctionne pas, filtrer côté client
      let filteredData = response.retourAchats || [];

      if (tabKey !== "tous" && Array.isArray(filteredData)) {
        if (tabKey === "unpaid") {
          filteredData = filteredData.filter(
            (retour) =>
              retour.payment_status === "Non payé" ||
              retour.due_amount === retour.total ||
              retour.paid_amount === 0
          );
        } else if (tabKey === "partially_paid") {
          filteredData = filteredData.filter(
            (retour) =>
              retour.payment_status === "Partiellement payé" ||
              (retour.paid_amount > 0 && retour.paid_amount < retour.total)
          );
        } else if (tabKey === "paid") {
          filteredData = filteredData.filter(
            (retour) =>
              retour.payment_status === "Payé" ||
              retour.paid_amount >= retour.total ||
              retour.due_amount === 0
          );
        }
      }

      // Mettre à jour les retours d'achat dans l'API
      retourAchatAPI.retourAchats = filteredData;

      setPagination((previous) => ({
        ...previous,
        current: page,
        pageSize,
        total: response.totalItems,
      }));
    } catch (error) {
      if (requestId !== retourAchatsRequestRef.current) return;
      console.error("Erreur lors du chargement des retours d'achat:", error);
      message.error("Erreur lors du chargement des retours d'achat");
    } finally {
      if (requestId === retourAchatsRequestRef.current) setLoading(false);
    }
  };

  // Charger les retours d'achat au chargement du composant et quand l'onglet change
  useEffect(() => {
    loadRetourAchats(1);
    return () => {
      retourAchatsRequestRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, filters, selectedCompany, selectedWarehouse, tabKey]);

  // Gérer le changement de page
  const handleTableChange = (nextPagination: any) => {
    const pageSize = nextPagination.pageSize;
    loadRetourAchats(
      pageSize === pagination.pageSize ? nextPagination.current : 1,
      pageSize
    );
  };

  // Appliquer les filtres
  const applyFilters = (values: any) => {
    // Si nous avons une plage de dates, s'assurer qu'elle est au bon format
    if (
      values.dateRange &&
      Array.isArray(values.dateRange) &&
      values.dateRange.length === 2
    ) {
      // Vérifier si les dates sont des objets dayjs valides
      if (values.dateRange[0] && values.dateRange[1]) {
      } else {
        values.dateRange = [null, null];
      }
    }

    setFilters({
      ...filters,
      ...values,
    });
    setFilterVisible(false);

    // Recharger les données avec les nouveaux filtres
    loadRetourAchats(1); // Retourner à la première page avec les nouveaux filtres
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilterVisible(false);

    // Recharger les données sans filtres
    loadRetourAchats(1);
  };

  // Configuration de l'expansion des lignes du tableau
  const expandable = {
    columnWidth: 44,
    expandedRowRender: (record: RetourAchat) => {
      const items = record.items || [];

      return (
        <Table
          rowKey={(item) => `${item.product_id}_${item.id || Math.random()}`}
          dataSource={items}
          pagination={false}
          size="small"
          columns={[
            {
              title: "Produit",
              dataIndex: "product_name",
              key: "product_name",
              render: (text, item) => {
                if (text) return text;
                const prod = produits.find((p) => p.id === item.product_id);
                return prod ? prod.name : `Produit #${item.product_id}`;
              },
            },
            {
              title: "Quantité",
              dataIndex: "quantity",
              key: "quantity",
              width: 100,
              render: (value) => formatQuantity(value),
            },
            {
              title: "Prix unitaire",
              dataIndex: "unit_price",
              key: "unit_price",
              width: 150,
              render: (value) => `${formatNumber(value)} FCFA`,
            },
            {
              title: "Remise",
              dataIndex: "discount_rate",
              key: "discount_rate",
              width: 100,
              render: (value) => `${value}%`,
            },
            {
              title: "Taxe",
              dataIndex: "tax_rate",
              key: "tax_rate",
              width: 100,
              render: (value) => `${value}%`,
            },
            {
              title: "Sous-total",
              dataIndex: "subtotal",
              key: "subtotal",
              width: 150,
              render: (value) => `${formatNumber(value)} FCFA`,
            },
          ]}
        />
      );
    },
    onExpand: async (expanded: boolean, record: RetourAchat) => {
      if (expanded) {
        // Si la ligne est étendue et que les articles ne sont pas encore chargés
        if (!record.items || record.items.length === 0) {
          // Charger les articles
          const items = await fetchRetourItems(record);

          // Mettre à jour la liste des retours d'achat pour inclure les articles
          const updatedRows = [...retourAchatAPI.retourAchats];
          const index = updatedRows.findIndex((r) => r.id === record.id);
          if (index !== -1) {
            updatedRows[index] = { ...updatedRows[index], items };
            // Ici, nous ne mettons pas à jour retourAchats directement car fetchRetourItems le fait déjà
          }
        }

        // Ajouter l'ID à la liste des lignes étendues
        setExpandedRowKeys((prev) => [...prev, record.id]);
      } else {
        // Supprimer l'ID de la liste des lignes étendues
        setExpandedRowKeys((prev) => prev.filter((key) => key !== record.id));
      }
    },
    expandedRowKeys,
  };

  // Fonction pour charger un retour d'achat pour modification
  const handleEdit = async (record: RetourAchat) => {
    // Vérifier si le retour a des paiements
    if (record.paid_amount > 0) {
      message.warning(
        "Ce retour d'achat a déjà des paiements enregistrés. Il n'est pas possible de le modifier."
      );
      return;
    }

    try {
      setLoading(true);

      // Récupérer les détails complets du retour d'achat
      const retourDetails = await retourAchatAPI.fetchRetourAchatDetail(
        String(record.id)
      );

      // Récupérer les articles du retour
      const retourItems = await retourAchatAPI.fetchOrderItems(
        String(record.id)
      );

      const retourComplet = {
        ...retourDetails,
        items: retourItems,
      };

      // Mettre à jour l'état et ouvrir le modal d'édition
      setRetourAchatToEdit(retourComplet);
      setEditModalVisible(true);
    } catch (error) {
      console.error(
        "Erreur lors du chargement du retour d'achat pour modification:",
        error
      );
      message.error(
        "Impossible de charger le retour d'achat pour modification"
      );
    } finally {
      setLoading(false);
    }
  };

  // Colonnes de la table
  const columns: TableProps<RetourAchat>["columns"] = [
    {
      title: "N° de facture",
      dataIndex: "invoice_number",
      key: "invoice_number",
      width: 160,
      render: (text: string, record: RetourAchat) => (
        <Button
          type="link"
          className="commercial-list__number"
          onClick={() => loadAchatDetails(record)}
        >
          {text}
        </Button>
      ),
    },
    {
      title: "Date du retour",
      dataIndex: "order_date",
      key: "order_date",
      render: (text: string) => dayjs(text).format("DD/MM/YYYY"),
      responsive: ["sm"],
      width: 110,
    },
    {
      title: "Fournisseur",
      key: "fournisseur",
      responsive: ["md"],
      render: (_: any, record: RetourAchat): React.ReactNode => {
        // Essayer d'abord d'utiliser le nom du fournisseur s'il est directement disponible
        if (record.user_name) {
          return record.user_name;
        }

        // Si l'ID du fournisseur est disponible, essayer de trouver dans la liste
        if (record.user_id && fournisseurs.length > 0) {
          // Essayer plusieurs correspondances possibles
          const supplier =
            fournisseurs.find((f) => Number(f.id) === Number(record.user_id)) ||
            fournisseurs.find(
              (f) =>
                f.user_id !== undefined &&
                Number(f.user_id) === Number(record.user_id)
            );

          if (supplier) {
            return supplier.name || "Nom inconnu";
          }
        }

        return "Fournisseur inconnu";
      },
      width: 180,
    },
    {
      title: "Établissement",
      dataIndex: "warehouse_name",
      key: "warehouse_name",
      responsive: ["lg"],
      width: 130,
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (text: number) => `${formatNumber(text)} FCFA`,
      width: 115,
    },
    {
      title: "Payé",
      dataIndex: "paid_amount",
      key: "paid_amount",
      align: "right",
      render: (text: number) => `${formatNumber(text)} FCFA`,
      responsive: ["md"],
      width: 115,
    },
    {
      title: "Restant",
      dataIndex: "due_amount",
      key: "due_amount",
      align: "right",
      render: (text: number) => `${formatNumber(text)} FCFA`,
      responsive: ["md"],
      width: 115,
    },
    {
      title: "Statut Paiement",
      dataIndex: "payment_status",
      key: "payment_status",
      responsive: ["sm"],
      width: 135,
      render: (text: string) => {
        let color = "default";
        if (text === "Payé") color = "success";
        else if (text === "Partiellement payé") color = "warning";
        else if (text === "Non payé") color = "error";
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Statut",
      dataIndex: "order_status",
      key: "order_status",
      responsive: ["sm"],
      width: 105,
      render: (text: string) => {
        let color = "default";
        if (text === "complété") color = "success";
        else if (text === "annulé") color = "error";
        else if (text === "en attente") color = "processing";
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 58,
      align: "center",
      render: (_: any, record: RetourAchat) => {
        const canView = checkPermission(
          "Gestion Commerciale.Approvisionnement.Achats.RetourAchat.view"
        );
        const canEdit = checkPermission(
          "Gestion Commerciale.Approvisionnement.Achats.RetourAchat.edit"
        );
        const canDelete = checkPermission(
          "Gestion Commerciale.Approvisionnement.Achats.RetourAchat.delete"
        );
        const isDeleted = record.is_deleted === 1;
        const normalizedStatus = String(record.order_status || "")
          .trim()
          .toLocaleLowerCase("fr-FR");
        const isCancelled = ["annulé", "annule"].includes(normalizedStatus);
        const editDisabled =
          isDeleted || isCancelled || !Boolean(record.is_deletable);
        const actionItems: any[] = [];

        if (canView) {
          actionItems.push({
            key: "view",
            icon: <EyeOutlined />,
            label: "Voir le détail",
            onClick: () => loadAchatDetails(record),
          });
        }

        if (canEdit) {
          actionItems.push({
            key: "edit",
            icon: <EditOutlined />,
            label: editDisabled ? "Modification indisponible" : "Modifier",
            disabled: editDisabled,
            onClick: () => handleEdit(record),
          });
        }

        if (canDelete) {
          if (actionItems.length > 0) {
            actionItems.push({ type: "divider" });
          }

          if (isDeleted) {
            actionItems.push({
              key: "restore",
              icon: <UndoOutlined />,
              label: "Restaurer",
              onClick: () => handleRestore(record.id),
            });
          } else if (isCancelled) {
            actionItems.push({
              key: "cancelled",
              icon: <CloseCircleOutlined />,
              label: "Retour d'achat déjà annulé",
              disabled: true,
            });
          } else {
            const deleteDisabled = !Boolean(record.is_deletable);
            actionItems.push(
              {
                key: "delete",
                icon: <DeleteOutlined />,
                label: deleteDisabled
                  ? "Suppression indisponible"
                  : "Supprimer",
                disabled: deleteDisabled,
                danger: !deleteDisabled,
                onClick: () => handleDelete(record.id),
              },
              {
                key: "cancel",
                icon: <CloseCircleOutlined />,
                label: "Annuler le retour d'achat",
                danger: true,
                onClick: () => handleCancel(record.id),
              }
            );
          }
        }

        return (
          <RowActionsMenu
            items={actionItems}
            loading={loading}
            ariaLabel={`Afficher les actions du retour d'achat ${
              record.invoice_number || record.id
            }`}
          />
        );
      },
    },
  ];

  return (
    <div className="commercial-list__table-wrap">
      <Table
        className="commercial-list__table"
        dataSource={retourAchatAPI.retourAchats}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        onChange={handleTableChange}
        expandable={expandable}
        scroll={{ x: 1290 }}
      />

      {/* Modal pour afficher les détails d'un retour d'achat */}
      <OrderDetailModal
        order={selectedRetourAchat}
        visible={detailModalVisible}
        onClose={() => setDetailModalVisible(false)}
        produits={produits}
        taxes={taxes}
        fournisseurs={fournisseurs}
        refreshOrderDetails={async () => {
          if (selectedRetourAchat) {
            await loadAchatDetails(selectedRetourAchat);
          }
        }}
        refreshRetourAchats={refreshRetourAchats}
      />

      {/* Modal pour éditer un retour d'achat */}
      <RetourAchatForm
        visible={editModalVisible}
        onClose={() => {
          setEditModalVisible(false);
          setRetourAchatToEdit(null);
        }}
        refreshRetourAchats={refreshRetourAchats}
        retourAchatToEdit={retourAchatToEdit}
        isEditing={true}
      />
    </div>
  );
};

// Composant principal qui intègre tous les autres composants
const GestionRetourAchat: React.FC = () => {
  const [retourAchatFormVisible, setRetourAchatFormVisible] = useState(false);
  const [state, dispatch] = useReducer(retourAchatsReducer, {
    retourAchats: [],
    fournisseurs: [],
    produits: [],
    taxes: [],
    loading: true,
    currentRetourAchat: null,
    retourAchatItems: [],
    orderPayments: [],
    warehouses: [],
    totalItems: 0,
    currentPage: 1,
    error: null,
    currentWarehouse: null,
    activeTab: "tous", // Initialiser activeTab
  });
  const { selectedWarehouse, selectedCompany } = useSelection(); // Récupérer l'entrepôt sélectionné du contexte
  // État local pour les filtres
  const [filters, setFilters] = useState({
    status: "",
    user_id: "",
    warehouse_id: "",
    dateRange: [null, null] as [any, any],
  });
  const [searchValue, setSearchValue] = useState("");
  // Utiliser la même approche simplifiée
  // @ts-ignore - Ignorer temporairement les vérifications de permission
  const auth = null;

  // Remplacer notre implémentation temporaire par le hook personnalisé
  const checkPermission = usePermission();

  // Debug de l'état de selectedWarehouse au niveau du composant principal
  useEffect(() => {}, [selectedWarehouse]);

  // Fonction pour rafraîchir la liste des retours d'achat
  const refreshRetourAchats = useCallback(async () => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });

      const params: any = {
        order_type: "purchase_return",
        page: state.currentPage,
        limit: 10,
        search: searchValue, // Use searchValue from parent component
      };

      // Add selected warehouse filter
      if (selectedWarehouse) {
        if (typeof selectedWarehouse === "object" && selectedWarehouse.id) {
          params.warehouse = selectedWarehouse.id;
        } else {
          params.warehouse = selectedWarehouse;
        }
      }

      // Add fournisseur filter
      if (filters.user_id) {
        params.user_id = filters.user_id;
      }

      // Add date range filter
      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        params.dateDebut = filters.dateRange[0].format("YYYY-MM-DD");
        params.dateFin = filters.dateRange[1].format("YYYY-MM-DD");
      }

      // Add payment status filter based on active tab
      if (state.activeTab === "unpaid") {
        params.payment_status = "Non payé";
      } else if (state.activeTab === "partially_paid") {
        params.payment_status = "Partiellement payé";
      } else if (state.activeTab === "paid") {
        params.payment_status = "Payé";
      }

      const api = new RetourAchatAPI();
      const response = await api.fetchRetourAchats(params);

      dispatch({
        type: "FETCH_RETOUR_ACHATS_SUCCESS",
        payload: response.retourAchats,
        totalItems: response.totalItems,
      });
    } catch (error) {
      console.error("Error refreshing return purchases:", error);
      dispatch({
        type: "SET_ERROR",
        payload: "Error loading return purchases",
      });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [
    state.currentPage,
    state.activeTab,
    selectedWarehouse,
    searchValue,
    filters,
  ]);

  // Refresh the data when component mounts or when dependencies change
  useEffect(() => {
    refreshRetourAchats();
  }, [refreshRetourAchats]);

  // Afficher un message si aucun entrepôt n'est sélectionné
  useEffect(() => {
    if (!selectedWarehouse) {
      message.info(
        "Veuillez sélectionner un établissement pour afficher les retours d'achat"
      );
    } else {
      // Récupérer la liste des fournisseurs au chargement du composant
      const fetchFournisseurs = async () => {
        try {
          const params: any = { warehouse: selectedWarehouse };
          const response = await retourAchatAPI.fetchFournisseurs(params);
          dispatch({
            type: "FETCH_FOURNISSEURS_SUCCESS",
            payload: response,
          });
        } catch (error) {
          console.error("Erreur lors du chargement des fournisseurs:", error);
          message.error("Erreur lors du chargement des fournisseurs");
        }
      };

      fetchFournisseurs();
    }
  }, [selectedWarehouse]);

  // Récupérer les taxes au chargement du composant
  useEffect(() => {
    const fetchTaxes = async () => {
      try {
        const response = await retourAchatAPI.fetchTaxes();
        dispatch({
          type: "FETCH_TAXES_SUCCESS",
          payload: response,
        });
      } catch (error) {
        console.error("Erreur lors du chargement des taxes:", error);
        message.error("Erreur lors du chargement des taxes");
      }
    };

    fetchTaxes();
  }, []);

  // Récupérer les entrepôts au chargement du composant
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await retourAchatAPI.fetchWarehouses();
        dispatch({
          type: "FETCH_WAREHOUSES_SUCCESS",
          payload: response,
        });
      } catch (error) {
        console.error("Erreur lors du chargement des établissements:", error);
        message.error("Erreur lors du chargement des établissements");
      }
    };

    fetchWarehouses();
  }, []);

  // Ouvrir le formulaire de création de retour d'achat
  const openRetourAchatForm = () => {
    setRetourAchatFormVisible(true);
  };

  // Fonction pour gérer le changement d'onglet
  const handleTabChange = (activeKey: string) => {
    // Mettre à jour le tabKey dans le state
    dispatch({ type: "SET_ACTIVE_TAB", payload: activeKey });

    // Réinitialiser la page courante à 1 via le state global
    dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });

    // Le rechargement des données se fera automatiquement via l'effet dans RetourAchatsTable
    // qui dépend de tabKey
  };

  const handleExportToExcel = async () => {
    if (!(await ensureSubscriptionActionAllowed('data_export'))) return;
    try {
      message.loading({
        content: "Préparation de l'export Excel...",
        key: "exportLoading",
      });

      // Préparer les paramètres avec tous les filtres appliqués
      const params: any = {
        page: 1,
        limit: 1000, // Augmenter la limite pour exporter plus de données
        order_type: "purchase_return",
      };

      // Ajouter le filtre par statut (onglet actif)
      if (state.activeTab && state.activeTab !== "tous") {
        if (state.activeTab === "unpaid") {
          params.payment_status = "Non payé";
        } else if (state.activeTab === "partially_paid") {
          params.payment_status = "Partiellement payé";
        } else if (state.activeTab === "paid") {
          params.payment_status = "Payé";
        }
      }

      // Ajouter le filtre par magasin
      if (selectedWarehouse) {
        if (typeof selectedWarehouse === "object" && selectedWarehouse.id) {
          params.warehouse_id = selectedWarehouse.id;
        } else {
          params.warehouse_id = selectedWarehouse;
        }
      }

      // Ajouter le filtre par fournisseur
      if (filters.user_id) {
        params.user_id = filters.user_id;
      }

      // Ajouter le filtre par date
      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        params.dateDebut = filters.dateRange[0].format("YYYY-MM-DD");
        params.dateFin = filters.dateRange[1].format("YYYY-MM-DD");
      }

      // Ajouter le filtre par recherche
      if (searchValue) {
        params.search = searchValue;
      }

      // Récupérer les données en tenant compte des filtres actuels
      const api = new RetourAchatAPI();
      const response = await api.fetchRetourAchats(params);
      const retours = response.retourAchats || [];

      // Préparer les données pour l'export Excel
      const dataToExport = retours.map((retour: RetourAchat) => ({
        ID: retour.id,
        "№ Facture": retour.invoice_number,
        Référence: retour.reference,
        Date: dayjs(retour.order_date).format("DD/MM/YYYY"),
        Fournisseur: retour.user_name,
        Établissement: retour.warehouse_name,
        Statut: retour.order_status,
        "Statut de paiement": retour.payment_status,
        "Montant payé": retour.paid_amount,
        "Montant dû": retour.due_amount,
        Total: retour.total,
        "Créé le": retour.created_at
          ? dayjs(retour.created_at).format("DD/MM/YYYY")
          : "-",
      }));

      // Créer le fichier Excel
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Retours d'achat");

      // Définir les largeurs des colonnes
      const columnWidths = [
        { wch: 8 }, // ID
        { wch: 15 }, // № Facture
        { wch: 15 }, // Référence
        { wch: 12 }, // Date
        { wch: 30 }, // Fournisseur
        { wch: 20 }, // Magasin
        { wch: 15 }, // Statut
        { wch: 20 }, // Statut de paiement
        { wch: 15 }, // Montant payé
        { wch: 15 }, // Montant dû
        { wch: 15 }, // Total
        { wch: 12 }, // Créé le
      ];
      worksheet["!cols"] = columnWidths;

      // Télécharger le fichier
      const today = dayjs().format("YYYY-MM-DD");
      XLSX.writeFile(workbook, `Retours_Achat_${today}.xlsx`);

      message.success({
        content: "Export Excel réussi!",
        key: "exportLoading",
      });
    } catch (error) {
      console.error("Erreur lors de l'export Excel:", error);
      message.error({
        content: "Erreur lors de l'export Excel",
        key: "exportLoading",
      });
    }
  };

  const displayedReturnTotals = state.retourAchats.reduce(
    (summary, retour) => ({
      total: summary.total + Number(retour.total || 0),
      paid: summary.paid + Number(retour.paid_amount || 0),
      due: summary.due + Number(retour.due_amount || 0),
    }),
    { total: 0, paid: 0, due: 0 }
  );
  const hasActiveListFilters = Boolean(
    searchValue.trim() ||
      filters.user_id ||
      filters.dateRange?.[0] ||
      filters.dateRange?.[1]
  );
  const listPeriodLabel =
    filters.dateRange?.[0] && filters.dateRange?.[1]
      ? `${dayjs(filters.dateRange[0]).format("DD/MM/YYYY")} – ${dayjs(
          filters.dateRange[1]
        ).format("DD/MM/YYYY")}`
      : "Toutes périodes";
  const resetListFilters = () => {
    setSearchValue("");
    setFilters({
      status: "",
      user_id: "",
      warehouse_id: "",
      dateRange: [null, null],
    });
    dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
  };
  const summaryMetrics = [
    {
      key: "count",
      label: "Retours trouvés",
      value: formatNumber(state.totalItems),
      icon: <BarChartOutlined />,
      tone: "primary" as const,
      description: "Nombre de retours correspondant aux filtres actifs.",
    },
    {
      key: "total",
      label: "Montant retourné (page)",
      value: `${formatNumber(displayedReturnTotals.total)} CFA`,
      icon: <UndoOutlined />,
      tone: "danger" as const,
      description: "Montant cumulé des retours visibles sur la page courante.",
    },
    {
      key: "recovered",
      label: "Montant récupéré (page)",
      value: `${formatNumber(displayedReturnTotals.paid)} CFA`,
      icon: <WalletOutlined />,
      tone: "success" as const,
      description: "Montant déjà récupéré sur les retours visibles.",
    },
    {
      key: "due",
      label: "Reste à récupérer (page)",
      value: `${formatNumber(displayedReturnTotals.due)} CFA`,
      icon: <ClockCircleOutlined />,
      tone: "warning" as const,
      description: "Solde à récupérer sur les retours visibles.",
    },
  ];

  return (
    <div className="commercial-list-page">
      <header className="commercial-list-page__header">
        <div className="commercial-list-page__heading">
          <h1>Retours d'achat</h1>
          <p>Suivre les avoirs et les remboursements de vos fournisseurs</p>
          <span className="commercial-list-page__scope">
            {formatNumber(state.totalItems)} retour
            {state.totalItems === 1 ? "" : "s"}
            <span aria-hidden="true">•</span>
            {listPeriodLabel}
          </span>
        </div>
        <div className="commercial-list-page__actions">
          {checkPermission(
            "Gestion Commerciale.Approvisionnement.Achats.RetourAchat.create"
          ) && (
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={openRetourAchatForm}
              className="commercial-list-page__primary-action"
            >
              Nouveau retour d'achat
            </Button>
          )}
        </div>
      </header>

      <CommercialListSummary
        ariaLabel="Situation des retours d'achat"
        metrics={summaryMetrics}
      />

      <section
        className="commercial-list-page__filters"
        aria-label="Filtres des retours d'achat"
      >
        <Input
          placeholder="Rechercher un numéro de retour ou un fournisseur"
          prefix={<SearchOutlined />}
          allowClear
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="commercial-list-page__search"
        />
        <Select
          placeholder="Tous les fournisseurs"
          className="commercial-list-page__entity-filter"
          value={filters.user_id || undefined}
          allowClear
          showSearch
          optionFilterProp="searchLabel"
          filterOption={(input, option: any) =>
            matchesCommercialEntitySearch(option?.searchLabel, input)
          }
          onChange={(value: any) => {
            setFilters({ ...filters, user_id: value || "" });
            dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
          }}
        >
          {state.fournisseurs.map((fournisseur) => (
            <Option
              key={fournisseur.id}
              value={fournisseur.id}
              searchLabel={buildCommercialEntitySearchText(fournisseur)}
            >
              {getCommercialEntityDisplayName(
                fournisseur,
                "Fournisseur inconnu"
              )}
            </Option>
          ))}
        </Select>
        <DatePicker.RangePicker
          value={filters.dateRange as any}
          format="DD/MM/YYYY"
          allowClear
          placeholder={["Début", "Fin"]}
          onChange={(dates: any) => {
            setFilters({ ...filters, dateRange: dates || [null, null] });
            dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
          }}
          className="commercial-list-page__period-filter"
        />
        <Button
          type="text"
          icon={<ReloadOutlined />}
          disabled={!hasActiveListFilters}
          onClick={resetListFilters}
          className="commercial-list-page__reset"
        >
          Réinitialiser
        </Button>
      </section>

      {state.error && (
        <Alert
          type="error"
          showIcon
          message={state.error}
          className="commercial-list-page__error"
        />
      )}

      <div className="commercial-list-page__tabs-toolbar">
        <Tabs
          defaultActiveKey="tous"
          activeKey={state.activeTab}
          onChange={handleTabChange}
          className="commercial-list-page__tabs"
        >
          <TabPane
            tab={`Tous les retours (${state.totalItems})`}
            key="tous"
          />
          <TabPane tab="Non payés" key="unpaid" />
          <TabPane tab="Partiellement payés" key="partially_paid" />
          <TabPane tab="Payés" key="paid" />
        </Tabs>
        {checkPermission(
          "Gestion Commerciale.Approvisionnement.Achats.RetourAchat.view"
        ) && (
          <Button
            icon={<FileExcelOutlined style={{ color: "#217346" }} />}
            onClick={handleExportToExcel}
            className="commercial-list-page__export"
          >
            Exporter sur Excel
          </Button>
        )}
      </div>

        <RetourAchatsTable
          refreshRetourAchats={refreshRetourAchats}
          tabKey={state.activeTab}
        />

      {/* Formulaire de création de retour d'achat */}
      <RetourAchatForm
        visible={retourAchatFormVisible}
        onClose={() => setRetourAchatFormVisible(false)}
        refreshRetourAchats={refreshRetourAchats}
        retourAchatToEdit={null}
        isEditing={false}
      />
    </div>
  );
};

export default GestionRetourAchat;
